import { isPlatformBrowser } from '@angular/common';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { KNOWN_TOP_LEVEL_PATHS } from '../constants/routing';

/** Adds X-Tenant-Id header to /api requests when the current URL is a path-based
 * tenant route (/:slug/...). Derives the slug from the live router URL at call-time
 * so there is no stale-state risk when navigating between routes.
 */
export const tenantHeaderInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!req.url.startsWith('/api')) {
    return next(req);
  }

  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  // Before the first navigation completes router.url is still '/'. Fall back
  // to the browser location so bootstrap-time requests get the tenant header.
  const path =
    router.url !== '/'
      ? router.url
      : isPlatformBrowser(platformId)
      ? document.location.pathname
      : router.url;
  const slug = extractSlugFromUrl(path);
  if (slug) {
    return next(req.clone({ setHeaders: { 'X-Tenant-Id': slug } }));
  }

  return next(req);
};

/** Returns the slug from a path-based route like /:slug/... or null otherwise. */
export function extractSlugFromUrl(url: string): string | null {
  const first = url.split('?')[0].split('/')[1];
  if (!first || KNOWN_TOP_LEVEL_PATHS.has(first)) {
    return null;
  }
  return first;
}
